import React, { useState, useEffect, useCallback, useRef } from 'react';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ChevronDown, Calendar, User, Layers, Loader2 } from 'lucide-react';
import { fetchCustomerItems, fetchPipeline } from '@/actions/pipeline';
import PipelineItemSheet from '@/components/pipeline/PipelineItemSheet';
import { CreatePipelineItemDialog } from '@/components/runner/customer/CreatePipelineItemDialog';
import type { PipelineItem } from '@/reducers/pipelineReducer';
import type { RootState } from '@/reducers';
import usePersistentOpen from '@/hooks/usePersistentOpen';

interface PipelineItemWithPipeline extends Omit<PipelineItem, '_pipeline' | '_assignee'> {
	_pipeline: { _id: string; name: string; columns?: any[] };
	_assignee?: string | { _id: string; name?: string } | null;
	columnName?: string;
	columnColor?: string;
}

// Conversation details remounts when switching conversations. Keep a bounded,
// business/customer-scoped snapshot so returning to the same customer is quiet.
const customerItemsCache = new Map<string, PipelineItemWithPipeline[]>();
const CACHE_LIMIT = 50;
const LOADING_DELAY_MS = 250;
type ItemsState = {
	key: string;
	items: PipelineItemWithPipeline[];
	loaded: boolean;
	loading: boolean;
	showLoading: boolean;
	error: string | null;
};
const initialState = (key: string): ItemsState => ({
	key,
	items: customerItemsCache.get(key) ?? [],
	loaded: customerItemsCache.has(key),
	loading: false,
	showLoading: false,
	error: null,
});

const PipelineItems = ({ _customer }: { _customer: string }) => {
	const dispatch = useDispatch();
	const businessId = useSelector((state: RootState) => state.auth?._id);
	const cacheKey = `${businessId ?? ''}:${_customer}`;
	const [isOpen, setIsOpen] = usePersistentOpen('conversation-details:pipeline-items');
	const [state, setState] = useState<ItemsState>(() => initialState(cacheKey));
	const [selection, setSelection] = useState<{
		key: string;
		item: PipelineItemWithPipeline;
	} | null>(null);
	const [sheetOpen, setSheetOpen] = useState(false);
	const activeKey = useRef(cacheKey);
	activeKey.current = cacheKey;
	const mounted = useRef(false);
	const requestId = useRef(0);
	const openingId = useRef(0);
	const loadingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const { currentPipeline } = useSelector((state: RootState) => state.pipeline);
	// Guard during render as well as request completion: a changed customer must
	// never receive even one frame of the previous customer's rows/count/error.
	const visible = state.key === cacheKey ? state : initialState(cacheKey);
	const selectedItem = selection?.key === cacheKey ? selection.item : null;

	useEffect(() => {
		mounted.current = true;
		return () => {
			mounted.current = false;
			requestId.current += 1;
			openingId.current += 1;
			if (loadingTimer.current) clearTimeout(loadingTimer.current);
		};
	}, []);

	const fetchItems = useCallback(async () => {
		if (!_customer || !mounted.current || activeKey.current !== cacheKey) return;
		const request = ++requestId.current;
		const isCurrent = () =>
			mounted.current && request === requestId.current && activeKey.current === cacheKey;
		if (loadingTimer.current) clearTimeout(loadingTimer.current);
		setState((previous) => ({
			...(previous.key === cacheKey ? previous : initialState(cacheKey)),
			loading: true,
			showLoading: false,
			error: null,
		}));
		loadingTimer.current = setTimeout(() => {
			if (isCurrent()) setState((previous) => ({ ...previous, showLoading: true }));
		}, LOADING_DELAY_MS);
		try {
			const result = (await dispatch(fetchCustomerItems(_customer) as any)) as any;
			if (!isCurrent()) return;
			if (!result?.success) throw new Error(result?.error || 'Could not load pipeline items.');
			const items = result.items as PipelineItemWithPipeline[];
			customerItemsCache.delete(cacheKey);
			customerItemsCache.set(cacheKey, items);
			if (customerItemsCache.size > CACHE_LIMIT)
				customerItemsCache.delete(customerItemsCache.keys().next().value!);
			setState({
				key: cacheKey,
				items,
				loaded: true,
				loading: false,
				showLoading: false,
				error: null,
			});
		} catch (error) {
			if (isCurrent())
				setState((previous) => ({
					...previous,
					error: 'Could not load pipeline items. Please try again.',
				}));
		} finally {
			if (isCurrent()) {
				if (loadingTimer.current) clearTimeout(loadingTimer.current);
				loadingTimer.current = null;
				setState((previous) => ({ ...previous, loading: false, showLoading: false }));
			}
		}
	}, [_customer, cacheKey, dispatch]);

	useEffect(() => {
		setSelection(null);
		setSheetOpen(false);
		openingId.current += 1;
		void fetchItems();
		return () => {
			requestId.current += 1;
			if (loadingTimer.current) clearTimeout(loadingTimer.current);
		};
	}, [fetchItems]);

	const changeOpen = (open: boolean) => {
		setIsOpen(open);
		// Refresh existing rows without removing them while the request runs.
		if (open && !visible.loading) void fetchItems();
	};

	const handleItemClick = async (item: PipelineItemWithPipeline) => {
		const request = ++openingId.current;
		if (!currentPipeline || currentPipeline._id !== item._pipeline._id) {
			await dispatch(fetchPipeline(item._pipeline._id) as any);
		}
		if (!mounted.current || activeKey.current !== cacheKey || request !== openingId.current) return;
		setSelection({ key: cacheKey, item });
		setSheetOpen(true);
	};

	return (
		<>
			<div className="shrink-0 border-b border-border/60">
				<Collapsible open={isOpen} onOpenChange={changeOpen}>
					{/* Phones: the whole row toggles, with the chevron on the right. */}
					<div className="flex w-full items-center justify-between px-4 py-3 max-md:relative max-md:min-h-12 max-md:py-1 max-md:pr-11">
						<CollapsibleTrigger asChild>
							<button
								type="button"
								className="group flex min-w-0 items-center gap-2 rounded-sm text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring max-md:absolute max-md:inset-0 max-md:flex-row-reverse max-md:justify-between max-md:rounded-none max-md:pl-4 max-md:pr-3 max-md:text-base max-md:transition-colors max-md:focus-visible:ring-inset max-md:active:bg-muted/60"
							>
								<ChevronDown className="size-4 text-muted-foreground transition-transform duration-150 group-data-[state=closed]:-rotate-90 max-md:size-5 max-md:text-muted-foreground/60" />
								<span className="max-md:min-w-0 max-md:truncate">
									Pipeline{visible.loaded ? ` (${visible.items.length})` : ''}
								</span>
							</button>
						</CollapsibleTrigger>
						<div className="md:contents max-md:relative max-md:z-[1] max-md:ml-auto max-md:flex max-md:[&>button]:size-11 max-md:[&>button]:rounded-full">
							<CreatePipelineItemDialog
								_customer={_customer}
								simplePlusButton
								onItemCreated={fetchItems}
							/>
						</div>
					</div>
					<CollapsibleContent>
						<div className="px-4 pb-3" aria-busy={visible.loading}>
							{visible.items.length > 0 && (
								<div className="space-y-2">
									{visible.items.map((item) => (
										<button
											type="button"
											key={item._id}
											onClick={() => void handleItemClick(item)}
											className="conversation-enter w-full rounded-xl border border-border/60 p-3 text-left transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
										>
											<h4 className="line-clamp-1 text-sm font-medium max-md:text-base">{item.name}</h4>
											{item.description && (
												<p className="mt-1 line-clamp-2 text-xs text-muted-foreground max-md:text-sm">
													{item.description}
												</p>
											)}
											<div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground max-md:text-xs">
												{item.columnName && (
													<span className="inline-flex min-w-0 items-center gap-1">
														<span
															className="size-1.5 shrink-0 rounded-full"
															style={{ backgroundColor: item.columnColor || '#e2e8f0' }}
														/>
														<span className="truncate">{item.columnName}</span>
													</span>
												)}
												<span className="inline-flex min-w-0 items-center gap-1">
													<Layers className="size-3 shrink-0" />
													<span className="max-w-[120px] truncate">{item._pipeline.name}</span>
												</span>
												{item._assignee && (
													<span className="inline-flex min-w-0 items-center gap-1">
														<User className="size-3 shrink-0" />
														<span className="max-w-[100px] truncate">
															{typeof item._assignee === 'object'
																? item._assignee?.name
																: 'Assigned'}
														</span>
													</span>
												)}
												{item.createdAt && (
													<span className="inline-flex items-center gap-1">
														<Calendar className="size-3 shrink-0" />
														{moment(item.createdAt).format('ll')}
													</span>
												)}
											</div>
											{item.priority && item.priority !== 'medium' && (
												<Badge variant="outline" className="mt-2 text-xs font-normal">
													{item.priority}
												</Badge>
											)}
										</button>
									))}
								</div>
							)}
							{visible.showLoading && (
								<p
									role="status"
									className="flex items-center gap-2 py-2 text-xs text-muted-foreground"
								>
									<Loader2 className="size-3 animate-spin motion-reduce:animate-none" />
									{visible.loaded ? 'Updating pipeline…' : 'Loading pipeline…'}
								</p>
							)}
							{visible.error && (
								<div role="alert" className="pt-2 text-xs text-muted-foreground">
									<p>{visible.error}</p>
									<Button
										type="button"
										variant="ghost"
										size="sm"
										onClick={() => void fetchItems()}
										className="mt-1 h-7 px-2 text-xs max-md:min-h-11 max-md:text-sm"
									>
										Try again
									</Button>
								</div>
							)}
							{visible.loaded && visible.items.length === 0 && !visible.error && (
								<p className="py-2 text-xs text-muted-foreground">
									No pipeline items yet. Use + to add one.
								</p>
							)}
						</div>
					</CollapsibleContent>
				</Collapsible>
			</div>
			{selectedItem && currentPipeline?._id === selectedItem._pipeline._id && (
				<PipelineItemSheet
					open={sheetOpen}
					onOpenChange={(open) => {
						setSheetOpen(open);
						if (!open) {
							setSelection(null);
							void fetchItems();
						}
					}}
					item={
						{
							...selectedItem,
							_pipeline: selectedItem._pipeline._id,
							_assignee:
								typeof selectedItem._assignee === 'object'
									? selectedItem._assignee?._id
									: selectedItem._assignee,
						} as PipelineItem
					}
					pipelineId={currentPipeline._id}
				/>
			)}
		</>
	);
};

export default PipelineItems;
